/**
 * SquatEvaluator — implementación del modelo squat_phases.
 *
 * Distinto del ExerciseEvaluator legacy (oscillating_angle, binario):
 *  - State machine LINEAL: awaiting_baseline → standing → descent → bottom → ascent → standing
 *  - Calibración individual baseline (2s standing antes de arrancar reps)
 *  - Tempo tracking por fase
 *  - Clasificación 3-level: ideal | acceptable | invalid
 *  - Thresholds RELATIVOS al baseline (no absolutos)
 *
 * Vista: lateral. Lee keypoints de un solo lado (default LEFT por
 * convención del schema). Ver docs/decisions/sentadilla-mini-calibracion.md
 * para el reasoning clínico completo.
 *
 * No depende de MediaPipe — recibe keypoints como input. Testeable con
 * mocks (ver SquatEvaluator.test-fixture.ts).
 */

import { KP } from "./types";
import type {
  BaselineSnapshot,
  EvaluationResult,
  Evaluator,
  ExerciseSchema,
  Keypoint,
  KeypointIndex,
  PoseResult,
  RepResult,
  SquatPhase,
} from "./types";
import { calc3DAngle, trunkAngle } from "./angles";

/** Versión del SquatEvaluator. Independiente del EXERCISE_EVALUATOR_VERSION
 *  legacy. Se persiste en pose_analysis.detector para trazabilidad. */
export const SQUAT_EVALUATOR_VERSION = "iter1";

/** Visibility mínima para considerar un keypoint utilizable.
 *  Lateral view tiene menos ghost zones que frontal (rot externa),
 *  threshold más estricto. */
const MIN_VISIBILITY = 0.5;

/** Mínima amplitud de movimiento para considerar que arrancó una rep.
 *  Si el paciente no flexiona la rodilla al menos N grados, no contamos.
 *  Tunable en QA físico. */
const MIN_PROMINENCE_DEGREES = 10;

/** Threshold de transición standing → descent: knee_flexion delta sobre
 *  baseline para considerar que arrancó el descenso. */
const DESCENT_TRIGGER_DELTA_DEG = 10;

/** Threshold de transición descent → bottom: knee_flexion debe estar
 *  por encima de acceptableMin del schema. */

/** Threshold de transición bottom → ascent: knee_flexion empieza a
 *  decrecer desde el max alcanzado. */
const ASCENT_TRIGGER_DECREASE_DEG = 5;

interface BaselineCapture {
  startedAt: number;
  kneeFlexionSamples: number[];
  hipFlexionSamples: number[];
  trunkAngleSamples: number[];
  visibilitySamples: number[];
}

interface RepInProgress {
  startedAt: number;
  phaseEnteredAt: Record<SquatPhase, number>;
  kneeFlexionMax: number;
  trunkLeanMax: number;
  hipFlexionMax: number;
  // Para detectar transición bottom → ascent (knee decrece desde max)
  kneeFlexionMaxAt: number;
}

interface CompletedPhases {
  descentMs: number;
  bottomHoldMs: number;
  ascentMs: number;
}

/** Lado del cuerpo a usar para los keypoints. Default LEFT por convención
 *  del schema (vista lateral del lado izquierdo del paciente). Cambiable
 *  en futuro si el schema declara `side: 'right'`. */
type Side = "left" | "right";

export class SquatEvaluator implements Evaluator {
  private schema: ExerciseSchema;
  private side: Side;

  // Keypoints del lado activo (resueltos en constructor)
  private kpShoulder: KeypointIndex;
  private kpHip: KeypointIndex;
  private kpKnee: KeypointIndex;
  private kpAnkle: KeypointIndex;

  // State
  private phase: SquatPhase = "awaiting_baseline";
  private phaseEnteredAt = 0;
  private baselineCapture: BaselineCapture | null = null;
  private baseline: BaselineSnapshot | null = null;
  private baselineEmittedOnce = false;
  private repCount = 0;
  private repInProgress: RepInProgress | null = null;
  // Tiempos de inicio de cada subfase de la rep en curso (para tempo)
  private completedPhases: CompletedPhases | null = null;

  /** Implementa Evaluator.version (interface común). */
  readonly version = SQUAT_EVALUATOR_VERSION;

  constructor(schema: ExerciseSchema, side: Side = "left") {
    if (schema.type !== "squat_phases") {
      throw new Error(
        `SquatEvaluator requires schema.type === 'squat_phases', got '${schema.type}' (schema id: ${schema.id})`,
      );
    }
    if (!schema.baseline || !schema.tempo || !schema.classification) {
      throw new Error(
        `SquatEvaluator schema missing required config (baseline/tempo/classification): ${schema.id}`,
      );
    }
    this.schema = schema;
    this.side = side;
    if (side === "left") {
      this.kpShoulder = KP.LEFT_SHOULDER;
      this.kpHip = KP.LEFT_HIP;
      this.kpKnee = KP.LEFT_KNEE;
      this.kpAnkle = KP.LEFT_ANKLE;
    } else {
      this.kpShoulder = KP.RIGHT_SHOULDER;
      this.kpHip = KP.RIGHT_HIP;
      this.kpKnee = KP.RIGHT_KNEE;
      this.kpAnkle = KP.RIGHT_ANKLE;
    }
    this.startBaselineCapture(0);
  }

  reset(): void {
    this.phase = "awaiting_baseline";
    this.phaseEnteredAt = 0;
    this.baselineCapture = null;
    this.baseline = null;
    this.baselineEmittedOnce = false;
    this.repCount = 0;
    this.repInProgress = null;
    this.completedPhases = null;
    this.startBaselineCapture(0);
  }

  evaluate(pose: PoseResult): EvaluationResult {
    const kp = pose.keypoints;
    const now = pose.timestamp;

    // Visibility gate — todos los keypoints relevantes deben estar visibles
    if (!this.areKeypointsVisible(kp)) {
      return this.emptyResult();
    }

    // Ángulos del frame
    const kneeFlexion = this.computeKneeFlexion(kp);
    const hipFlexion = this.computeHipFlexion(kp);
    const trunkLean = this.computeTrunkLean(kp);

    // Branch por fase del state machine
    if (this.phase === "awaiting_baseline") {
      return this.processBaseline(now, kneeFlexion, hipFlexion, trunkLean, kp);
    }

    // Post-baseline: el procesamiento es relativo al baseline capturado
    if (!this.baseline) return this.emptyResult();

    const kneeFlexionDelta = kneeFlexion - this.baseline.kneeFlexion;
    const trunkLeanAbs = trunkLean; // trunk lean es absoluto (desde vertical), no relativo a baseline
    const hipFlexionDelta = hipFlexion - this.baseline.hipFlexion;

    // Track máximos de la rep en curso
    if (this.repInProgress) {
      this.repInProgress.kneeFlexionMax = Math.max(
        this.repInProgress.kneeFlexionMax,
        kneeFlexionDelta,
      );
      this.repInProgress.trunkLeanMax = Math.max(
        this.repInProgress.trunkLeanMax,
        trunkLeanAbs,
      );
      this.repInProgress.hipFlexionMax = Math.max(
        this.repInProgress.hipFlexionMax,
        hipFlexionDelta,
      );
      if (kneeFlexionDelta >= this.repInProgress.kneeFlexionMax) {
        this.repInProgress.kneeFlexionMaxAt = now;
      }
    }

    const completedRep = this.updatePhaseStateMachine(
      now,
      kneeFlexionDelta,
      trunkLeanAbs,
    );

    return {
      ruleEvaluations: [],
      repCount: this.repCount,
      repPhase: this.phase,
      completedRep,
    };
  }

  // ============================================================================
  // Baseline capture
  // ============================================================================

  private startBaselineCapture(now: number): void {
    this.baselineCapture = {
      startedAt: now,
      kneeFlexionSamples: [],
      hipFlexionSamples: [],
      trunkAngleSamples: [],
      visibilitySamples: [],
    };
    this.phase = "awaiting_baseline";
    this.phaseEnteredAt = now;
  }

  private processBaseline(
    now: number,
    kneeFlexion: number,
    hipFlexion: number,
    trunkLean: number,
    kp: Keypoint[],
  ): EvaluationResult {
    const cap = this.baselineCapture!;
    cap.kneeFlexionSamples.push(kneeFlexion);
    cap.hipFlexionSamples.push(hipFlexion);
    cap.trunkAngleSamples.push(trunkLean);
    cap.visibilitySamples.push(this.averageVisibility(kp));

    const elapsed = now - cap.startedAt;
    const durationMs = this.schema.baseline!.durationMs;
    const progress = Math.min(1, elapsed / durationMs);

    if (elapsed >= durationMs) {
      const knee = avg(cap.kneeFlexionSamples);
      const hip = avg(cap.hipFlexionSamples);
      const trunk = avg(cap.trunkAngleSamples);
      const visibility = avg(cap.visibilitySamples);

      // Gate de standing real. `knee` aquí es FLEXION (0° = recto),
      // `standingMaxKneeFlexion` también está en flexion → la rodilla
      // está "parado" si la flexión observada NO supera ese máximo.
      const baselineCfg = this.schema.baseline!;
      const isStandingValid =
        knee <= baselineCfg.standingMaxKneeFlexion &&
        trunk <= baselineCfg.standingMaxTrunkLean;

      if (!isStandingValid) {
        // Falla del gate — reseteamos el capture para que el paciente
        // vuelva a intentar. La UI puede leer baselineProgress=0 + ausencia
        // de baselineSnapshot para mostrar mensaje corrector.
        console.warn("[SquatEvaluator] baseline gate failed", {
          kneeFlex: knee,
          trunk,
          maxKneeFlex: baselineCfg.standingMaxKneeFlexion,
          maxTrunk: baselineCfg.standingMaxTrunkLean,
        });
        this.startBaselineCapture(now);
        return {
          ruleEvaluations: [],
          repCount: 0,
          repPhase: this.phase,
          baselineProgress: 0,
        };
      }

      this.baseline = {
        kneeFlexion: knee,
        hipFlexion: hip,
        trunkAngle: trunk,
        visibilityScore: visibility,
        capturedAt: now,
      };
      this.phase = "standing";
      this.phaseEnteredAt = now;
      this.baselineEmittedOnce = true;

      return {
        ruleEvaluations: [],
        repCount: 0,
        repPhase: this.phase,
        baselineSnapshot: this.baseline,
      };
    }

    return {
      ruleEvaluations: [],
      repCount: 0,
      repPhase: this.phase,
      baselineProgress: progress,
    };
  }

  // ============================================================================
  // State machine de fases del squat
  // ============================================================================

  private updatePhaseStateMachine(
    now: number,
    kneeFlexionDelta: number,
    trunkLeanAbs: number,
  ): RepResult | undefined {
    const cfg = this.schema.classification!;

    switch (this.phase) {
      case "standing": {
        // Trigger descent: knee_flexion sube por encima del trigger delta
        if (kneeFlexionDelta >= DESCENT_TRIGGER_DELTA_DEG) {
          this.repInProgress = {
            startedAt: now,
            phaseEnteredAt: {
              awaiting_baseline: 0,
              standing: 0,
              descent: now,
              bottom: 0,
              ascent: 0,
            },
            kneeFlexionMax: kneeFlexionDelta,
            trunkLeanMax: trunkLeanAbs,
            hipFlexionMax: 0,
            kneeFlexionMaxAt: now,
          };
          this.completedPhases = null;
          this.transitionTo("descent", now);
        }
        break;
      }

      case "descent": {
        // Trigger bottom: knee_flexion alcanza acceptable zone
        if (kneeFlexionDelta >= cfg.knee.acceptableMin) {
          const descentMs = now - this.repInProgress!.phaseEnteredAt.descent;
          this.completedPhases = {
            descentMs,
            bottomHoldMs: 0,
            ascentMs: 0,
          };
          this.repInProgress!.phaseEnteredAt.bottom = now;
          this.transitionTo("bottom", now);
        }
        // Si el paciente cancela el descenso y vuelve a top sin llegar al bottom,
        // abortamos la rep
        if (kneeFlexionDelta < DESCENT_TRIGGER_DELTA_DEG / 2) {
          console.warn("[SquatEvaluator] rep aborted: descent reversed");
          this.repInProgress = null;
          this.completedPhases = null;
          this.transitionTo("standing", now);
        }
        break;
      }

      case "bottom": {
        // Trigger ascent: knee_flexion empieza a decrecer desde el max
        const decreaseFromMax =
          this.repInProgress!.kneeFlexionMax - kneeFlexionDelta;
        if (decreaseFromMax >= ASCENT_TRIGGER_DECREASE_DEG) {
          const bottomHoldMs =
            now - this.repInProgress!.phaseEnteredAt.bottom;
          this.completedPhases!.bottomHoldMs = bottomHoldMs;
          this.repInProgress!.phaseEnteredAt.ascent = now;
          this.transitionTo("ascent", now);
        }
        break;
      }

      case "ascent": {
        // Trigger end of rep: knee_flexion vuelve cerca de standing (within
        // returnToTopWithinDeg del baseline)
        if (kneeFlexionDelta <= cfg.phase.returnToTopWithinDeg) {
          const ascentMs = now - this.repInProgress!.phaseEnteredAt.ascent;
          this.completedPhases!.ascentMs = ascentMs;
          const rep = this.completeRep(now);
          this.transitionTo("standing", now);
          return rep;
        }
        // Si el paciente se queda en ascent demasiado tiempo sin llegar a top,
        // marcamos como fase incompleta y completamos la rep igual
        const ascentElapsed = now - this.repInProgress!.phaseEnteredAt.ascent;
        if (ascentElapsed > 3000) {
          this.completedPhases!.ascentMs = ascentElapsed;
          const rep = this.completeRep(now, true);
          this.transitionTo("standing", now);
          return rep;
        }
        break;
      }

      case "awaiting_baseline":
        // No debería llegar acá — processBaseline maneja este caso
        break;
    }

    return undefined;
  }

  private transitionTo(phase: SquatPhase, now: number): void {
    this.phase = phase;
    this.phaseEnteredAt = now;
  }

  private completeRep(now: number, phaseIncomplete = false): RepResult {
    const rip = this.repInProgress!;
    const phases = this.completedPhases!;
    const cfg = this.schema.classification!;
    const tempo = this.schema.tempo!;
    this.repCount++;

    const reasons: string[] = [];
    type Level = "ideal" | "acceptable" | "invalid";
    let level: Level = "ideal" as Level;
    const violations: string[] = [];

    // 1) Profundidad (knee flexion)
    if (rip.kneeFlexionMax < cfg.knee.acceptableMin) {
      level = "invalid";
      reasons.push("depth_too_shallow");
      violations.push(cfg.knee.invalidReasonShallow);
    } else if (rip.kneeFlexionMax > cfg.knee.acceptableMax) {
      level = "invalid";
      reasons.push("depth_too_deep");
      violations.push(cfg.knee.invalidReasonTooDeep);
    } else if (
      rip.kneeFlexionMax < cfg.knee.idealMin ||
      rip.kneeFlexionMax > cfg.knee.idealMax
    ) {
      if (level !== "invalid") level = "acceptable";
      reasons.push("depth_acceptable");
    } else {
      reasons.push("depth_target");
    }

    // 2) Trunk lean
    if (rip.trunkLeanMax > cfg.trunk.warningMax) {
      level = "invalid";
      reasons.push("trunk_critical");
      violations.push(cfg.trunk.invalidReason);
    } else if (rip.trunkLeanMax > cfg.trunk.idealMax) {
      if (level !== "invalid") level = "acceptable";
      reasons.push("trunk_warning");
    }

    // 3) Fase incompleta
    if (phaseIncomplete) {
      level = "invalid";
      reasons.push("phase_incomplete");
      violations.push(cfg.phase.invalidReason);
    }

    // 4) Tempo
    if (phases.descentMs < tempo.descentMinMsCritical) {
      level = "invalid";
      reasons.push("tempo_critical_descent");
      violations.push("Bajaste demasiado rápido");
    } else if (phases.descentMs < tempo.descentMinMsWarning) {
      if (level !== "invalid") level = "acceptable";
      reasons.push("tempo_warning_descent");
    }

    if (phases.bottomHoldMs < tempo.bottomHoldMinMsCritical) {
      level = "invalid";
      reasons.push("tempo_critical_bottom_bounce");
      violations.push("Rebotaste abajo sin mantener");
    } else if (phases.bottomHoldMs < tempo.bottomHoldMinMsWarning) {
      if (level !== "invalid") level = "acceptable";
      reasons.push("tempo_warning_bottom");
    }

    if (phases.ascentMs < tempo.ascentMinMsCritical) {
      level = "invalid";
      reasons.push("tempo_critical_ascent");
      violations.push("Subiste demasiado rápido");
    } else if (phases.ascentMs < tempo.ascentMinMsWarning) {
      if (level !== "invalid") level = "acceptable";
      reasons.push("tempo_warning_ascent");
    }

    const totalRepMs = now - rip.startedAt;
    if (totalRepMs < tempo.totalMinMsCritical) {
      level = "invalid";
      reasons.push("tempo_critical_total");
    } else if (totalRepMs < tempo.totalMinMsWarning) {
      if (level !== "invalid") level = "acceptable";
      reasons.push("tempo_warning_total");
    }

    // 5) Prominence — la amplitud del movimiento debe ser significativa
    if (rip.kneeFlexionMax < MIN_PROMINENCE_DEGREES) {
      level = "invalid";
      reasons.push("prominence_too_low");
      violations.push("Movimiento muy chico para contar");
    }

    const quality =
      level === "ideal" ? 1.0 : level === "acceptable" ? 0.7 : 0.0;

    const rep: RepResult = {
      n: this.repCount,
      angleMax: rip.kneeFlexionMax,
      durationMs: totalRepMs,
      quality,
      violations,
      angleAtStart: 0, // relativo al baseline = 0
      prominence: rip.kneeFlexionMax,
      valid: level !== "invalid",
      level,
      squatDetails: {
        kneeFlexionMax: round2(rip.kneeFlexionMax),
        trunkLeanMax: round2(rip.trunkLeanMax),
        hipFlexionMax: round2(rip.hipFlexionMax),
        descentMs: phases.descentMs,
        bottomHoldMs: phases.bottomHoldMs,
        ascentMs: phases.ascentMs,
        reasons,
      },
    };

    this.repInProgress = null;
    this.completedPhases = null;
    return rep;
  }

  // ============================================================================
  // Cálculo de ángulos
  // ============================================================================

  /** Flexión de rodilla — ángulo en el vértice de la rodilla entre
   *  hip→knee y knee→ankle. Convertido a flexión: 180° - ángulo medido,
   *  donde 0° = pierna extendida y 180° = totalmente flexionada. */
  private computeKneeFlexion(kp: Keypoint[]): number {
    const a = kp[this.kpHip];
    const v = kp[this.kpKnee];
    const b = kp[this.kpAnkle];
    const measured = calc3DAngle(a, v, b);
    return 180 - measured;
  }

  /** Flexión de cadera — ángulo entre shoulder→hip y hip→knee.
   *  Convertido a flexión igual que knee. */
  private computeHipFlexion(kp: Keypoint[]): number {
    const a = kp[this.kpShoulder];
    const v = kp[this.kpHip];
    const b = kp[this.kpKnee];
    const measured = calc3DAngle(a, v, b);
    return 180 - measured;
  }

  /** Trunk lean — ángulo del vector shoulder→hip desde la vertical.
   *  0° = tronco perfectamente vertical, 90° = horizontal. Reutiliza
   *  el helper trunkAngle pero con los keypoints del lado lateral. */
  private computeTrunkLean(kp: Keypoint[]): number {
    // Para vista lateral usamos solo un lado. trunkAngle del helper toma
    // los 4 keypoints (ambos lados); acá los duplicamos del lado activo
    // para que el cálculo funcione consistente.
    const shoulder = kp[this.kpShoulder];
    const hip = kp[this.kpHip];
    return trunkAngle(shoulder, shoulder, hip, hip);
  }

  private areKeypointsVisible(kp: Keypoint[]): boolean {
    return (
      kp[this.kpShoulder]?.visibility >= MIN_VISIBILITY &&
      kp[this.kpHip]?.visibility >= MIN_VISIBILITY &&
      kp[this.kpKnee]?.visibility >= MIN_VISIBILITY &&
      kp[this.kpAnkle]?.visibility >= MIN_VISIBILITY
    );
  }

  private averageVisibility(kp: Keypoint[]): number {
    const v =
      kp[this.kpShoulder].visibility +
      kp[this.kpHip].visibility +
      kp[this.kpKnee].visibility +
      kp[this.kpAnkle].visibility;
    return v / 4;
  }

  private emptyResult(): EvaluationResult {
    return {
      ruleEvaluations: [],
      repCount: this.repCount,
      repPhase: this.phase,
    };
  }

  // Getters para tests / debug
  getPhase(): SquatPhase {
    return this.phase;
  }

  getBaseline(): BaselineSnapshot | null {
    return this.baseline;
  }

  getRepCount(): number {
    return this.repCount;
  }
}

// ── helpers ──────────────────────────────────────────────────────────────

function avg(nums: number[]): number {
  if (nums.length === 0) return 0;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
